import type { ReactElement, ReactNode } from "react"
import { memo } from "react"
import PageFooterTemplate from "@onestore/hel/dist/components/templates/PageFooterTemplate"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import type { FooterText } from "@gatsby-plugin-generic-page/fragments/footerText"
import { SquidexComponentType } from "@gatsby-plugin-generic-page/types/componentType"
import { typeMatches } from "@onestore-graphql"
import type { ModalWithButtonsData } from "~/fragments/modalWithButtons"
import useFooterData from "~/hooks/useFooterData"
import useSiteMetadataQuery from "~/hooks/useSiteMetadataQuery"
import { brand } from "~/lib/config"
import isEmpty from "~/lib/isEmpty"
import AdditionalFooter from "./AdditionalFooter"

interface FooterProps {
  hideMobileFooter?: boolean
}

function FooterTemplate({
  hideMobileFooter = false,
}: FooterProps): ReactElement | null {
  const hasToggleableCategoriesOnSmall = brand === "home"

  const { logo, socials, partners, categories, additionalFooterData } =
    useFooterData()

  const {
    headerData: { logo: logoSize },
  } = useSiteMetadataQuery()

  let footerAdditionalData: ReactNode

  if (!isEmpty(additionalFooterData)) {
    if (
      typeMatches(
        additionalFooterData.__typename,
        SquidexComponentType.FOOTER_TEXT
      )
    ) {
      const data = additionalFooterData as FooterText

      footerAdditionalData = (
        <MarkdownText
          pushSpace={0}
          typography={{ bold: "nano1", default: "nano2" }}
          color="baseInverted"
        >
          {data.text}
        </MarkdownText>
      )
    }

    if (
      typeMatches(
        additionalFooterData.__typename,
        SquidexComponentType.MODAL_WITH_BUTTONS_COMPONENT
      )
    ) {
      const data = additionalFooterData as ModalWithButtonsData

      footerAdditionalData = <AdditionalFooter additionalFooterData={data} />
    }
  }

  const footer = (
    <PageFooterTemplate
      logo={{ ...logo, ...logoSize }}
      socials={socials}
      categories={categories}
      partners={partners}
      footer={footerAdditionalData}
      hasToggleableCategoriesOnSmall={hasToggleableCategoriesOnSmall}
    />
  )

  return hideMobileFooter ? null : footer
}

export default memo(FooterTemplate)
